import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router";
import { useAppSelector } from "../../store";
import { ENUM_USER_ROLE } from "../../enums";
import RecordingList from "./list";
import { useMutationWithDispatch } from "../../hooks/useMutationWithDispatch";
import { getAmbassadorInvitationRecordings } from "../../api/invitations";
import { Stack } from "@mui/material";
import Filters from "./filters";
import { PrimaryButton } from "../../components/form";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { appToast } from "../../components/notification/app-toast";
import { saveAs } from "file-saver";
import {
  buildConsolidatedStatementPdfForList,
  PdfScopeBlock,
} from "./pdf/consolidated-statement";
import { convertToFullName } from "../../services/utils";
import NotAuthorized from "../../templates/not-authorized";

export default function MeetingRecordings() {
  const { setPageConfig }: any = useOutletContext();
  const user = useAppSelector((state) => state.auth.user);
  const profile = useAppSelector((state) => state.profile?.data);
  const isDelegateVendor = user?.role === ENUM_USER_ROLE.DELEGATE_VENDOR;
  const userType =
    user?.role === ENUM_USER_ROLE.AMBASSADOR ? "client" : "ambassador";
  const [filters, setFilters] = useState({
    from: "",
    to: "",
    clientId: "",
    ambassadorId: "",
  });
  const {
    mutate: fetchRecordings,
    data: recordings,
    isPending,
    isError,
  } = useMutationWithDispatch(getAmbassadorInvitationRecordings, {
    showErrorToast: true,
  });

  const recordingsListRef = useRef<unknown[]>([]);
  const viewerRoleRef = useRef(user?.role);
  const pdfScopeRef = useRef<PdfScopeBlock | null>(null);

  useEffect(() => {
    viewerRoleRef.current = user?.role;
  }, [user?.role]);

  useEffect(() => {
    const raw = recordings?.data;
    recordingsListRef.current = Array.isArray(raw) ? raw : [];
  }, [recordings]);

  useEffect(() => {
    const base = profile || user;
    if (!base) {
      pdfScopeRef.current = null;
      return;
    }
    const name = convertToFullName(base).trim();
    const title = (base as { title?: string }).title?.trim() || "";
    const company = (base as { company?: string }).company?.trim() || "";

    pdfScopeRef.current = {
      primary: name || "User",
      secondary: title || undefined,
      tertiary: company || undefined,
    };
  }, [profile, user]);

  useEffect(() => {
    if (isDelegateVendor) return;

    setPageConfig({
      title: "Meeting Recordings",
      headerActionItem: (
        <PrimaryButton
          type="button"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={() => {
            const list = recordingsListRef.current as unknown[];
            if (!list.length) {
              appToast.error("No recordings available to download.");
              return;
            }
            try {
              const blob = buildConsolidatedStatementPdfForList({
                recordings: list as any[],
                viewerRole:
                  viewerRoleRef.current ?? ENUM_USER_ROLE.AMBASSADOR,
                pdfScope: pdfScopeRef.current,
              });
              saveAs(blob, `consolidated-statement_${list.length}-meetings.pdf`);
            } catch (e) {
              appToast.error(
                "Failed to generate consolidated summary. Please try again later."
              );
            }
          }}
        >
          Consolidate Summary
        </PrimaryButton>
      ),
    });

    fetchRecordings(filters);
  }, []);

  const handleFilterChange = (updatedFilters) => {
    const payload = {
      from: updatedFilters.dateRange?.from,
      to: updatedFilters.dateRange?.to,
      clientId:
        user?.role === ENUM_USER_ROLE.AMBASSADOR ? updatedFilters?.user : "",
      ambassadorId:
        user?.role === ENUM_USER_ROLE.VENDOR ? updatedFilters?.user : "",
    };

    setFilters(payload);
    fetchRecordings(payload);
  };

  if (isDelegateVendor) {
    return <NotAuthorized />;
  }

  return (
    <Stack gap={2}>
      <Filters onChange={handleFilterChange} />
      <RecordingList
        list={recordings?.data}
        fetching={isPending}
        role={userType}
        isError={isError}
      />
    </Stack>
  );
}
